import {
  formatDistanceKm,
  resolveAvailableSpots,
  resolveEstimatedArrivalLabel,
  resolveLivePassengerCount
} from '@smarttransit/common'
import { addAlert } from '../../utilities/helpers'
import {
  getPassengersByTransportationId,
  getTransportationProfileGeolocationMetadata
} from '../../services/transportation-profiles-service'
import { getBusStopsForProfileRoute } from '../../services/transportation-profile-routes-service'
import { getManifestPassengerTrips } from '../../services/passenger-trips-service'
import { getBoardedPassengerProfileIds, resolvePassedBusStopIndex } from '@smarttransit/common-client'
import { buildLivePassengerDisplayRows } from './format-live-passengers'

const LIVE_STALE_THRESHOLD_MS = 4 * 60 * 1000
const LIVE_FEED_POLL_INTERVAL_MS = 5000

const TRANSPORT_TYPE_LABELS = {
  smarttransit_minibus: 'SmartTransit Mini Bus',
  smarttransit_bus: 'SmartTransit Bus',
  minibus: 'Minibus'
}

function formatTransportType (transportType) {
  if (!transportType) {
    return 'Unknown'
  }

  if (TRANSPORT_TYPE_LABELS[transportType]) {
    return TRANSPORT_TYPE_LABELS[transportType]
  }

  return transportType
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function formatSpeed (speedPerHour) {
  if (speedPerHour === null || speedPerHour === undefined || speedPerHour === '') {
    return 'Unknown'
  }

  const speed = Number(speedPerHour)
  if (Number.isNaN(speed)) {
    return 'Unknown'
  }

  return `${Math.round(speed)} km/h`
}

function hasFreshTimestamp (timestamp) {
  const timestampMs = timestamp ? new Date(timestamp).getTime() : NaN
  return !Number.isNaN(timestampMs) && (Date.now() - timestampMs) <= LIVE_STALE_THRESHOLD_MS
}

function getErrorMessage (err, fallback) {
  if (err && err.error && err.error.message) {
    return err.error.message
  }
  if (err && err.message) {
    return err.message
  }
  return fallback
}

export default {
  name: 'live-passengers-dialog',
  props: {
    vehicle: {
      type: Object,
      default: null
    },
    isLive: {
      type: Boolean,
      default: undefined
    },
    passengerCount: {
      type: Number,
      default: undefined
    },
    buttonClass: {
      type: String,
      default: 'ma-0 elevation-1'
    },
    hideTrigger: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialogOpen: false,
      apiInProgress: false,
      telemetryInProgress: false,
      loadError: null,
      telemetryError: null,
      telemetryMetadata: null,
      passengers: [],
      passengerHash: [],
      passengerTrips: [],
      routeBusStops: [],
      routeBusStopsRouteId: null,
      routeBusStopsRequestId: 0,
      passedBusStopIndex: -1,
      liveFeedPollHandle: null,
      liveFeedRequestId: 0,
      passengersPollInProgress: false,
      telemetryPollInProgress: false
    }
  },
  computed: {
    hasVehicle () {
      return !!(this.vehicle && this.vehicle.id)
    },
    resolvedIsLive () {
      if (typeof this.isLive === 'boolean') {
        return this.isLive
      }
      if (this.telemetryMetadata && this.telemetryMetadata.lastDetectedTimestamp) {
        return hasFreshTimestamp(this.telemetryMetadata.lastDetectedTimestamp)
      }
      return !!(this.vehicle && this.vehicle.isLive)
    },
    resolvedPassengerCount () {
      if (typeof this.passengerCount === 'number') {
        return this.passengerCount
      }
      if (this.vehicle && typeof this.vehicle.livePassengerCount === 'number') {
        return this.vehicle.livePassengerCount
      }
      return null
    },
    buttonTitle () {
      if (!this.resolvedIsLive) {
        return 'Vehicle is not live'
      }
      const count = this.resolvedPassengerCount
      return `View ${typeof count === 'number' ? count : 0} live passenger(s)`
    },
    vehicleLabel () {
      if (!this.vehicle) {
        return ''
      }
      return this.vehicle.licensePlateNumber || this.vehicle.shortcodeId || this.vehicle.id || ''
    },
    showTrigger () {
      return !this.hideTrigger && this.hasVehicle
    },
    equipmentIdLabel () {
      const equipment = this.vehicle && this.vehicle.transportationEquipment
      if (equipment && equipment.deviceId) {
        return equipment.deviceId
      }
      if (this.vehicle && this.vehicle.transportationEquipmentId) {
        return this.vehicle.transportationEquipmentId
      }
      return 'Unknown'
    },
    equipmentLabel () {
      const equipment = this.vehicle && this.vehicle.transportationEquipment
      if (!equipment) {
        return ''
      }
      return equipment.inventoryId || equipment.label || equipment.name || ''
    },
    vehicleTypeLabel () {
      return this.vehicle && this.vehicle.vehicleTypeLabel
        ? this.vehicle.vehicleTypeLabel
        : formatTransportType(this.vehicle && this.vehicle.transportType)
    },
    vehicleTypeIcon () {
      const label = `${this.vehicleTypeLabel} ${this.vehicle && this.vehicle.transportType ? this.vehicle.transportType : ''}`.toLowerCase()
      return label.indexOf('mini') !== -1 || label.indexOf('van') !== -1
        ? 'fas fa-shuttle-van'
        : 'fas fa-bus'
    },
    liveStatusLabel () {
      return this.resolvedIsLive ? 'Live' : 'Offline'
    },
    livePulseStatus () {
      return this.resolvedIsLive ? 'success' : 'off'
    },
    previousStopLabel () {
      return this.telemetryMetadata && this.telemetryMetadata.lastGeolocationLabel
        ? this.telemetryMetadata.lastGeolocationLabel
        : null
    },
    nextBusStopLabel () {
      if (this.telemetryMetadata && this.telemetryMetadata.nextBusStopId && this.telemetryMetadata.nextGeolocationLabel) {
        return this.telemetryMetadata.nextGeolocationLabel
      }
      return null
    },
    estimatedNextStopArrivalLabel () {
      if (!this.telemetryMetadata) {
        return null
      }
      const arrivalLabel = resolveEstimatedArrivalLabel({
        distanceKm: this.telemetryMetadata.distanceToNextStop,
        speedKmPerHour: this.telemetryMetadata.speedPerHour
      })
      return arrivalLabel || null
    },
    distanceToNextStopLabel () {
      if (!this.telemetryMetadata) {
        return null
      }
      const distanceLabel = formatDistanceKm(this.telemetryMetadata.distanceToNextStop)
      return distanceLabel || null
    },
    currentSpeedLabel () {
      return this.telemetryMetadata
        ? formatSpeed(this.telemetryMetadata.speedPerHour)
        : 'Unknown'
    },
    capacityPassengerCount () {
      return resolveLivePassengerCount({
        metadata: this.telemetryMetadata,
        passengers: this.passengers
      })
    },
    availableSpots () {
      return resolveAvailableSpots({
        transportationProfile: this.vehicle,
        transportType: this.vehicle && this.vehicle.transportType,
        passengerCount: this.capacityPassengerCount
      })
    },
    availableSpotsLabel () {
      if (!this.availableSpots || !this.availableSpots.supported) {
        return null
      }
      return this.availableSpots.label
    }
  },
  beforeDestroy () {
    this.stopLiveFeedPolling()
  },
  watch: {
    dialogOpen (isOpen) {
      if (isOpen) {
        this.startLiveFeedPolling()
        return
      }

      this.stopLiveFeedPolling()
      this.resetLiveFeedState()
    },
    'vehicle.id' (vehicleId, previousVehicleId) {
      if (!this.dialogOpen || !vehicleId || vehicleId === previousVehicleId) {
        return
      }

      this.passengers = []
      this.passengerHash = []
      this.passengerTrips = []
      this.routeBusStops = []
      this.routeBusStopsRouteId = null
      this.routeBusStopsRequestId += 1
      this.passedBusStopIndex = -1
      this.loadError = null
      this.telemetryMetadata = null
      this.telemetryError = null
      // Restart so in-flight responses for the previous vehicle are invalidated.
      this.startLiveFeedPolling()
    }
  },
  methods: {
    openDialog () {
      if (!this.hasVehicle) {
        return
      }
      this.dialogOpen = true
    },
    closeDialog () {
      this.dialogOpen = false
    },
    resetLiveFeedState () {
      this.passengers = []
      this.passengerHash = []
      this.passengerTrips = []
      this.routeBusStops = []
      this.routeBusStopsRouteId = null
      this.routeBusStopsRequestId += 1
      this.passedBusStopIndex = -1
      this.loadError = null
      this.apiInProgress = false
      this.passengersPollInProgress = false
      this.telemetryMetadata = null
      this.telemetryError = null
      this.telemetryInProgress = false
      this.telemetryPollInProgress = false
    },
    // How far along the itinerary the vehicle is, carried between polls. `nextBusStopId` is
    // derived from the vehicle's position on the route line rather than from an observed
    // arrival, so a stop passed before the dialog was opened still counts.
    trackPassedBusStopIndex () {
      const metadata = this.telemetryMetadata || {}

      this.passedBusStopIndex = resolvePassedBusStopIndex({
        busStops: this.routeBusStops,
        lastBusStopId: metadata.lastBusStopId || null,
        nextBusStopId: metadata.nextBusStopId || null,
        previousPassedBusStopIndex: this.passedBusStopIndex
      })
    },
    rebuildPassengerRows () {
      const transportationProfileId = this.vehicle && this.vehicle.id
      if (!transportationProfileId) {
        this.passengers = []
        return
      }

      const metadata = this.telemetryMetadata || {}
      this.trackPassedBusStopIndex()

      this.passengers = buildLivePassengerDisplayRows({
        passengerHash: this.passengerHash,
        passengerTrips: this.passengerTrips,
        transportationProfileId,
        busStops: this.routeBusStops,
        lastBusStopId: metadata.lastBusStopId || null,
        nextBusStopId: metadata.nextBusStopId || null,
        passedBusStopIndex: this.passedBusStopIndex,
        transportationRouteIdList: metadata.currentRouteIdList || null
      })
    },
    async ensureRouteBusStops ({ transportationProfileRouteId, requestId }) {
      if (!transportationProfileRouteId) {
        this.routeBusStops = []
        this.routeBusStopsRouteId = null
        this.passedBusStopIndex = -1
        return
      }

      // A different route means a different stop order, so progress measured against the old
      // one no longer means anything.
      if (this.routeBusStopsRouteId && this.routeBusStopsRouteId !== transportationProfileRouteId) {
        this.passedBusStopIndex = -1
      }

      if (this.routeBusStopsRouteId === transportationProfileRouteId && this.routeBusStops.length) {
        return
      }

      const busStopsRequestId = ++this.routeBusStopsRequestId
      try {
        const busStops = await getBusStopsForProfileRoute({ id: transportationProfileRouteId })
        if (!this.isLiveFeedRequestCurrent(requestId) || busStopsRequestId !== this.routeBusStopsRequestId) {
          return
        }

        this.routeBusStops = Array.isArray(busStops) ? busStops : []
        this.routeBusStopsRouteId = transportationProfileRouteId
      } catch (err) {
        if (!this.isLiveFeedRequestCurrent(requestId) || busStopsRequestId !== this.routeBusStopsRequestId) {
          return
        }

        // Keep prior stops if the route is unchanged; otherwise clear so we do not apply a stale itinerary.
        if (this.routeBusStopsRouteId !== transportationProfileRouteId) {
          this.routeBusStops = []
          this.routeBusStopsRouteId = null
          this.passedBusStopIndex = -1
        }
      }
    },
    startLiveFeedPolling () {
      this.stopLiveFeedPolling()
      this.refreshLiveFeed({ showLoading: true })
      this.liveFeedPollHandle = setInterval(() => {
        this.refreshLiveFeed({ showLoading: false })
      }, LIVE_FEED_POLL_INTERVAL_MS)
    },
    stopLiveFeedPolling () {
      if (this.liveFeedPollHandle) {
        clearInterval(this.liveFeedPollHandle)
        this.liveFeedPollHandle = null
      }
      // Invalidate any in-flight responses from a previous open/poll cycle.
      this.liveFeedRequestId += 1
      this.routeBusStopsRequestId += 1
      this.passengersPollInProgress = false
      this.telemetryPollInProgress = false
      this.apiInProgress = false
      this.telemetryInProgress = false
    },
    refreshLiveFeed ({ showLoading }) {
      this.loadPassengers({ showLoading: !!showLoading })
      this.loadTelemetry({ showLoading: !!showLoading })
    },
    isLiveFeedRequestCurrent (requestId) {
      return this.dialogOpen && requestId === this.liveFeedRequestId
    },
    viewPassengerProfile (passenger) {
      if (!passenger || !passenger.passengerProfileId) {
        return
      }

      this.closeDialog()
      this.$router.push({
        name: 'passengers-passenger',
        params: { passengerId: passenger.passengerProfileId }
      })
    },
    hasPassengerWarning (passenger) {
      if (!passenger) {
        return false
      }
      return Boolean(
        passenger.expiresTimestamp ||
        !passenger.userPayStatus ||
        passenger.extremelyLowBalance ||
        passenger.lowBalance
      )
    },
    async loadPassengers ({ showLoading } = { showLoading: true }) {
      const transportationProfileId = this.vehicle && this.vehicle.id
      if (!transportationProfileId) {
        this.loadError = 'No vehicle selected'
        this.passengers = []
        this.passengerHash = []
        this.passengerTrips = []
        return
      }

      if (this.passengersPollInProgress) {
        return
      }

      const requestId = this.liveFeedRequestId
      this.passengersPollInProgress = true
      if (showLoading) {
        this.apiInProgress = true
        this.loadError = null
      }

      try {
        // Map API Redis passenger hash for this transportation profile is the boarded authority.
        const passengerHash = await getPassengersByTransportationId(transportationProfileId)
        if (!this.isLiveFeedRequestCurrent(requestId)) {
          return
        }

        const boardedPassengerProfileIds = getBoardedPassengerProfileIds(passengerHash)
        const passengerTrips = await getManifestPassengerTrips({
          transportationProfileId,
          boardedPassengerProfileIds
        })
        if (!this.isLiveFeedRequestCurrent(requestId)) {
          return
        }

        this.passengerHash = Array.isArray(passengerHash) ? passengerHash : []
        this.passengerTrips = Array.isArray(passengerTrips) ? passengerTrips : []
        this.rebuildPassengerRows()
        this.loadError = null
      } catch (err) {
        if (!this.isLiveFeedRequestCurrent(requestId)) {
          return
        }

        this.loadError = getErrorMessage(err, 'Failed to load live passengers')
        if (showLoading || !this.passengers.length) {
          this.passengers = []
          this.passengerHash = []
          this.passengerTrips = []
          addAlert({
            message: `Error loading live passengers: ${this.loadError}`,
            type: 'error'
          })
        }
      } finally {
        if (requestId === this.liveFeedRequestId) {
          this.passengersPollInProgress = false
          this.apiInProgress = false
        }
      }
    },
    async loadTelemetry ({ showLoading } = { showLoading: true }) {
      const transportationProfileId = this.vehicle && this.vehicle.id
      if (!transportationProfileId) {
        this.telemetryMetadata = null
        return
      }

      if (this.telemetryPollInProgress) {
        return
      }

      const requestId = this.liveFeedRequestId
      this.telemetryPollInProgress = true
      if (showLoading) {
        this.telemetryInProgress = true
        this.telemetryError = null
      }

      try {
        const telemetryMetadata = await getTransportationProfileGeolocationMetadata({
          transportationProfileId,
          shortcodeId: this.vehicle.shortcodeId
        })
        if (!this.isLiveFeedRequestCurrent(requestId)) {
          return
        }

        this.telemetryMetadata = telemetryMetadata
        this.telemetryError = null
        await this.ensureRouteBusStops({
          transportationProfileRouteId: telemetryMetadata && telemetryMetadata.transportationProfileRouteId
            ? telemetryMetadata.transportationProfileRouteId
            : null,
          requestId
        })
        if (!this.isLiveFeedRequestCurrent(requestId)) {
          return
        }

        // Re-apply prepaid destination filtering once lastBusStopId / itinerary are known.
        this.rebuildPassengerRows()
      } catch (err) {
        if (!this.isLiveFeedRequestCurrent(requestId)) {
          return
        }

        this.telemetryError = getErrorMessage(err, 'Failed to load telemetry')
        if (showLoading || !this.telemetryMetadata) {
          this.telemetryMetadata = null
        }
      } finally {
        if (requestId === this.liveFeedRequestId) {
          this.telemetryPollInProgress = false
          this.telemetryInProgress = false
        }
      }
    }
  }
}
